// Página "laudo do pedido": veredito + saúde + alertas primeiro, evidência por
// fonte depois (venda ML, dinheiro MP/extrato, fiscal NF-e), matéria-prima
// (raw) por último. A interpretação vem do backend (/order/:id/analysis —
// motor orderAnalysis); a camada de auditoria (ApiValueTree com path+cor por
// fonte) continua intacta na seção "Dados técnicos".
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'shared/icons';
import api from '../../services/api';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatMoney } from '../../utils/format';
import PageHeader from '../layout/PageHeader';
import StatusBadge from '../StatusBadge';
import Button from '../ui/Button';
import ErrorCard from '../ui/ErrorCard';
import AlertsSection from './AlertsSection';
import AnalysisSummary, { OverallBadge, RebateChip } from './AnalysisSummary';
import { ApiValue, ApiValueTree } from './ApiValue';
import FiscalSection from './FiscalSection';
import { RebateRefreshButton } from './HealthCell';
import MoneyComposition from './MoneyComposition';
import OrderTimeline from './OrderTimeline';

// Aliases locais: `money`/`dateTime` são passados como prop `format` em ~20
// ApiValue abaixo — a implementação é a compartilhada de utils/format|date.
const money = (v) => FormatMoney(v);
const dateTime = (v) => FormatDateTimeBr(v);

function Section({ title, children, defaultOpen = true }) {
    return (
        <details
            open={defaultOpen}
            className="rounded-xl border border-mono-200 bg-white dark:border-mono-700 dark:bg-mono-900"
        >
            <summary className="cursor-pointer px-4 py-3 font-semibold">{title}</summary>
            <div className="border-t border-mono-100 p-4 dark:border-mono-800">{children}</div>
        </details>
    );
}

function ChargesTable({ charges }) {
    if (!charges?.length) return <p className="text-sm text-mono-400">Sem charges_details.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
                <thead>
                    <tr className="text-left text-xs text-mono-500">
                        <th className="py-1 pr-2">name</th>
                        <th className="pr-2">type</th>
                        <th className="pr-2 text-right">amounts.original</th>
                        <th className="pr-2 text-right">amounts.refunded</th>
                        <th className="pr-2">accounts (from → to)</th>
                        <th>metadata.campaign_type</th>
                    </tr>
                </thead>
                <tbody>
                    {charges.map((c, i) => (
                        <tr
                            key={c.id || `${c.name}-${c.amounts?.original}`}
                            className="border-t border-mono-100 dark:border-mono-800"
                        >
                            <td className="py-1 pr-2 font-mono text-[#3483fa]" title={`charges_details[${i}].name`}>
                                {c.name}
                            </td>
                            <td className="pr-2 font-mono text-[#3483fa]" title={`charges_details[${i}].type`}>
                                {c.type}
                            </td>
                            <td
                                className="pr-2 text-right font-mono text-[#3483fa]"
                                title={`charges_details[${i}].amounts.original`}
                                data-api-path={`charges_details[${i}].amounts.original`}
                                data-source="mp"
                            >
                                {money(c.amounts?.original)}
                            </td>
                            <td
                                className="pr-2 text-right font-mono text-[#3483fa]"
                                title={`charges_details[${i}].amounts.refunded`}
                            >
                                {money(c.amounts?.refunded)}
                            </td>
                            <td
                                className="pr-2 font-mono text-xs text-[#3483fa]"
                                title={`charges_details[${i}].accounts`}
                            >
                                {c.accounts?.from} → {c.accounts?.to}
                            </td>
                            <td
                                className="font-mono text-xs text-mono-400"
                                title={`charges_details[${i}].metadata.campaign_type`}
                            >
                                {c.metadata?.campaign_type || '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function MovementsSection({ movements }) {
    if (!movements?.length) {
        return (
            <p className="text-sm text-mono-400">
                Nenhum lançamento do extrato MP pra esta order. Importe um Released Money report que cubra o período
                (botão flutuante na lista de pagamentos).
            </p>
        );
    }
    const fee = (v) => (v == null || Number(v) === 0 ? '' : money(v));
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
                <thead>
                    <tr className="text-left text-xs text-mono-500">
                        <th className="py-1 pr-3">Data</th>
                        <th className="pr-3">Descrição</th>
                        <th className="pr-3 text-right">MP fee</th>
                        <th className="pr-3 text-right">Frete</th>
                        <th className="pr-3 text-right">Cupom</th>
                        <th className="pr-3 text-right">Crédito</th>
                        <th className="text-right">Débito</th>
                    </tr>
                </thead>
                <tbody>
                    {movements.map((m) => (
                        <tr key={m.id} className="border-t border-mono-100 dark:border-mono-800">
                            <td className="whitespace-nowrap py-1 pr-3 text-xs text-mono-500">
                                {dateTime(m.movement_date)}
                            </td>
                            <td className="pr-3">
                                <span className="font-medium text-teal-600 dark:text-teal-400">
                                    {m.description || '—'}
                                </span>
                                {m.record_type && m.record_type !== m.description ? (
                                    <span className="ml-1 text-xs text-mono-400">{m.record_type}</span>
                                ) : null}
                            </td>
                            <td className="pr-3 text-right font-mono text-xs text-mono-500">{fee(m.mp_fee_amount)}</td>
                            <td className="pr-3 text-right font-mono text-xs text-mono-500">
                                {fee(m.shipping_fee_amount)}
                            </td>
                            <td className="pr-3 text-right font-mono text-xs text-mono-500">{fee(m.coupon_amount)}</td>
                            <td className="pr-3 text-right font-mono text-emerald-600 dark:text-emerald-400">
                                {Number(m.net_credit_amount) > 0 ? `+ ${money(m.net_credit_amount)}` : ''}
                            </td>
                            <td className="text-right font-mono text-red-600 dark:text-red-400">
                                {Number(m.net_debit_amount) > 0 ? `− ${money(m.net_debit_amount)}` : ''}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function PaymentOrderReport({ orderId, embedded = false }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [movements, setMovements] = useState([]);
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        const sequence = ++requestSeq.current;
        setLoading(true);
        setError(null);
        setData(null);
        setAnalysis(null);
        try {
            const [detail, movs, anls] = await Promise.all([
                api.getPaymentOrderDetail(orderId),
                api.getOrderMovements(orderId),
                api.getOrderAnalysis(orderId),
            ]);
            if (sequence !== requestSeq.current) return;
            setData(detail);
            setMovements(Array.isArray(movs) ? movs : []);
            setAnalysis(anls);
        } catch (err) {
            if (sequence === requestSeq.current) setError(err.message || 'Erro ao carregar');
        } finally {
            if (sequence === requestSeq.current) setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        load();
        return () => {
            requestSeq.current += 1;
        };
    }, [load]);

    const criticalAnchors = new Set(
        (analysis?.rules || []).filter((r) => r.severity === 'critical').map((r) => r.anchor),
    );

    return (
        <div className="space-y-4">
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-2">
                        {embedded ? 'Pagamentos detalhados' : `Order ${orderId}`}
                        {analysis ? <OverallBadge overall={analysis.health.overall} /> : null}
                    </span>
                }
                subtitle="Laudo do pedido — âmbar = Mercado Livre · azul = MercadoPago · teal = extrato · violeta = NF-e"
                actions={
                    <div className="flex gap-2">
                        {!embedded ? (
                            <Button variant="secondary" onClick={() => navigate('/payments')}>
                                <ArrowLeft size={18} className="mr-2" />
                                Voltar
                            </Button>
                        ) : null}
                        <Button variant="secondary" onClick={load}>
                            Atualizar
                        </Button>
                        <RebateRefreshButton orderId={orderId} onRefreshed={load} />
                    </div>
                }
            />

            {error ? <ErrorCard message={error} /> : null}

            {loading ? (
                <p className="text-mono-500">Carregando…</p>
            ) : error ? null : !data && !analysis ? (
                <p className="text-mono-500">Order não encontrada no DB local.</p>
            ) : (
                <>
                    {analysis?.reconciliation?.status === 'verify' ? (
                        <p className="border-l-4 border-cyan-600 bg-cyan-50 px-4 py-3 text-sm text-cyan-900 dark:bg-cyan-950 dark:text-cyan-200">
                            Verificar · conciliado automaticamente. A conferência humana continua pendente.
                        </p>
                    ) : analysis?.reconciliation?.status === 'not_applicable' ? (
                        <p className="border-l-4 border-mono-400 bg-mono-50 px-4 py-3 text-sm text-mono-700 dark:bg-mono-900 dark:text-mono-300">
                            Conciliação dispensada · cancelamento sem movimentação financeira.
                        </p>
                    ) : null}
                    <AnalysisSummary analysis={analysis} />
                    <AlertsSection rules={analysis?.rules} />

                    {analysis ? (
                        <Section title="Linha do tempo">
                            <OrderTimeline timeline={analysis.timeline} />
                        </Section>
                    ) : null}

                    <Section title="A venda — Mercado Livre">
                        {data?.order ? (
                            <>
                                <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                                    <ApiValue label="Status" value={data.order.status} source="ml" path="status" />
                                    <ApiValue
                                        label="Total"
                                        value={data.order.total_amount}
                                        source="ml"
                                        path="total_amount"
                                        format={money}
                                    />
                                    <ApiValue
                                        label="Pago"
                                        value={data.order.paid_amount}
                                        source="ml"
                                        path="paid_amount"
                                        format={money}
                                    />
                                    <ApiValue
                                        label="Comprador"
                                        value={data.order.buyer?.nickname}
                                        source="ml"
                                        path="buyer.nickname"
                                    />
                                </div>
                                {(data.order.order_items || []).map((it, i) => (
                                    <div
                                        key={it.item?.id || `item-${i}`}
                                        className="mb-2 grid grid-cols-2 gap-3 rounded-lg bg-mono-50 p-2 md:grid-cols-5 dark:bg-mono-800"
                                    >
                                        <ApiValue
                                            label="SKU"
                                            value={it.item?.seller_sku}
                                            source="ml"
                                            path={`order_items[${i}].item.seller_sku`}
                                        />
                                        <ApiValue
                                            label="MLB"
                                            value={it.item?.id}
                                            source="ml"
                                            path={`order_items[${i}].item.id`}
                                        />
                                        <ApiValue
                                            label="Qtd"
                                            value={it.quantity}
                                            source="ml"
                                            path={`order_items[${i}].quantity`}
                                        />
                                        <ApiValue
                                            label="Unit price"
                                            value={it.unit_price}
                                            source="ml"
                                            path={`order_items[${i}].unit_price`}
                                            format={money}
                                        />
                                        <ApiValue
                                            label="Sale fee"
                                            value={it.sale_fee}
                                            source="ml"
                                            path={`order_items[${i}].sale_fee`}
                                            format={money}
                                        />
                                    </div>
                                ))}
                            </>
                        ) : (
                            <p className="text-sm text-mono-400">Order não está no DB local (só os payments).</p>
                        )}
                    </Section>

                    {/* Entre a VENDA e o DINHEIRO de propósito: o rebate desta
                        campanha não é uma entrada de caixa, é a comissão que o
                        ML deixou de cobrar na cascata logo abaixo. */}
                    {analysis?.rebate ? <RebateChip rebate={analysis.rebate} /> : null}

                    <Section title="Dinheiro — pagamento e recebimento">
                        {analysis ? (
                            <MoneyComposition money={analysis.money} completeness={analysis.completeness} />
                        ) : null}

                        {(data?.payments || []).length > 0 ? (
                            <div className="mt-4 space-y-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-mono-500">
                                    Pagamentos ({data.payments.length}) — MercadoPago
                                </p>
                                {data.payments.map((p) => {
                                    const mp = p.raw || {};
                                    const td = mp.transaction_details || {};
                                    return (
                                        <div
                                            key={p.id}
                                            className="rounded-xl border border-[#3483fa]/30 bg-white p-3 dark:bg-mono-900"
                                        >
                                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                                <span className="font-mono font-bold text-[#3483fa]" title="id">
                                                    {p.id}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <StatusBadge domain="payment" status={mp.status} />
                                                    <span className="text-xs text-mono-400" title="status_detail">
                                                        {mp.status_detail}
                                                    </span>
                                                    <span className="text-xs text-mono-400" title="date_created">
                                                        {dateTime(mp.date_created)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mb-2 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                                                <ApiValue
                                                    label="Transação"
                                                    value={mp.transaction_amount}
                                                    source="mp"
                                                    path="transaction_amount"
                                                    format={money}
                                                />
                                                <ApiValue
                                                    label="Cupom"
                                                    value={mp.coupon_amount}
                                                    source="mp"
                                                    path="coupon_amount"
                                                    format={money}
                                                />
                                                <ApiValue
                                                    label="Comprador pagou"
                                                    value={td.total_paid_amount}
                                                    source="mp"
                                                    path="transaction_details.total_paid_amount"
                                                    format={money}
                                                />
                                                <ApiValue
                                                    label="Líquido previsto"
                                                    value={td.net_received_amount}
                                                    source="mp"
                                                    path="transaction_details.net_received_amount"
                                                    format={money}
                                                />
                                                <ApiValue
                                                    label="Estornado"
                                                    value={mp.transaction_amount_refunded}
                                                    source="mp"
                                                    path="transaction_amount_refunded"
                                                    format={money}
                                                />
                                                <ApiValue
                                                    label="Liberação"
                                                    value={mp.money_release_status}
                                                    source="mp"
                                                    path="money_release_status"
                                                />
                                            </div>
                                            <details>
                                                <summary className="cursor-pointer text-xs text-mono-500">
                                                    Tarifas / cupons / frete (charges_details) e refunds
                                                </summary>
                                                <div className="mt-2">
                                                    <ChargesTable charges={mp.charges_details} />
                                                    {(mp.refunds || []).length > 0 ? (
                                                        <div className="mt-3">
                                                            <p className="mb-1 text-xs font-semibold text-mono-500">
                                                                Devoluções (refunds)
                                                            </p>
                                                            {mp.refunds.map((r, i) => (
                                                                <div
                                                                    key={r.id || `refund-${i}`}
                                                                    className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4"
                                                                >
                                                                    <ApiValue
                                                                        label="amount"
                                                                        value={r.amount}
                                                                        source="mp"
                                                                        path={`refunds[${i}].amount`}
                                                                        format={money}
                                                                    />
                                                                    <ApiValue
                                                                        label="status"
                                                                        value={r.status}
                                                                        source="mp"
                                                                        path={`refunds[${i}].status`}
                                                                    />
                                                                    <ApiValue
                                                                        label="date_created"
                                                                        value={r.date_created}
                                                                        source="mp"
                                                                        path={`refunds[${i}].date_created`}
                                                                        format={dateTime}
                                                                    />
                                                                    <ApiValue
                                                                        label="id"
                                                                        value={r.id}
                                                                        source="mp"
                                                                        path={`refunds[${i}].id`}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </details>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="mt-2 text-sm text-mono-400">Nenhum pagamento registrado para esta order.</p>
                        )}

                        <details className="mt-4" open={criticalAnchors.has('extrato')}>
                            <summary className="cursor-pointer text-xs text-mono-500">
                                Extrato completo (Released Money report) — {movements.length} lançamento(s)
                            </summary>
                            <div className="mt-2">
                                <MovementsSection movements={movements} />
                            </div>
                        </details>
                    </Section>

                    <Section title="Fiscal — NF-e" defaultOpen>
                        <FiscalSection analysis={analysis} orderId={orderId} />
                    </Section>

                    {data?.order_shipment ? (
                        <Section title="Logística — envio" defaultOpen={false}>
                            <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                                <ApiValue
                                    label="Status"
                                    value={data.order_shipment.status}
                                    source="ml"
                                    path="shipment.status"
                                />
                                <ApiValue
                                    label="Tipo logístico"
                                    value={data.order_shipment.logistic_type || data.order_shipment.logistic?.type}
                                    source="ml"
                                    path="shipment.logistic_type"
                                />
                                <ApiValue
                                    label="Enviado em"
                                    value={data.order_shipment.status_history?.date_shipped}
                                    source="ml"
                                    path="shipment.status_history.date_shipped"
                                    format={dateTime}
                                />
                                <ApiValue
                                    label="Entregue em"
                                    value={data.order_shipment.status_history?.date_delivered}
                                    source="ml"
                                    path="shipment.status_history.date_delivered"
                                    format={dateTime}
                                />
                            </div>
                        </Section>
                    ) : null}

                    <Section title="Dados técnicos — raw e auditoria" defaultOpen={false}>
                        <p className="mb-3 text-xs text-mono-400">
                            Cada valor tem o path exato da API no tooltip (pra conferir no Insomnia). Cores: âmbar = ML
                            · azul = MP · teal = extrato · violeta = NF-e.
                        </p>
                        <div className="space-y-3">
                            {data?.order ? (
                                <details>
                                    <summary className="cursor-pointer text-sm font-medium text-amber-600 dark:text-amber-400">
                                        Order raw (ML)
                                    </summary>
                                    <div className="mt-2">
                                        <ApiValueTree data={data.order} source="ml" />
                                    </div>
                                </details>
                            ) : null}
                            {data?.order_shipment ? (
                                <details>
                                    <summary className="cursor-pointer text-sm font-medium text-amber-600 dark:text-amber-400">
                                        Shipment raw (ML)
                                    </summary>
                                    <div className="mt-2">
                                        <ApiValueTree data={data.order_shipment} source="ml" />
                                    </div>
                                </details>
                            ) : null}
                            {(data?.payments || []).map((p) => (
                                <details key={`raw-${p.id}`}>
                                    <summary className="cursor-pointer text-sm font-medium text-[#3483fa]">
                                        Payment {p.id} raw (MP)
                                    </summary>
                                    <div className="mt-2">
                                        <ApiValueTree data={p.raw || {}} source="mp" />
                                    </div>
                                </details>
                            ))}
                            {(analysis?.fiscal?.vendas || []).concat(analysis?.fiscal?.devolucoes || []).map((inv) => (
                                <details key={`nfe-${inv.chave}`}>
                                    <summary className="cursor-pointer text-sm font-medium text-violet-600 dark:text-violet-400">
                                        NF-e {inv.numero}/{inv.serie} parsed (nfe_documents + itens)
                                    </summary>
                                    <div className="mt-2">
                                        <ApiValueTree data={inv} source="nfe" />
                                    </div>
                                </details>
                            ))}
                        </div>
                    </Section>
                </>
            )}
        </div>
    );
}
